import { useCallback, useEffect, useState } from 'react'
import {
  ALL_ACCEPTED_PREFERENCES,
  DEFAULT_PREFERENCES,
  type CookieCategory,
  type CookieConsentRecord,
  type CookiePreferences,
} from '@/types/cookieConsent'
import {
  applyConsentedScripts,
  loadCookieConsent,
  saveCookieConsent,
} from '@/utils/cookieStorage'

export interface UseCookieConsentReturn {
  hasConsented: boolean
  preferences: CookiePreferences
  showBanner: boolean
  showModal: boolean
  acceptAll: () => void
  rejectOptional: () => void
  openPreferences: () => void
  closePreferences: () => void
  savePreferences: (preferences: CookiePreferences) => void
  isCategoryEnabled: (category: CookieCategory) => boolean
}

export function useCookieConsent(): UseCookieConsentReturn {
  const [record, setRecord] = useState<CookieConsentRecord | null>(() => loadCookieConsent())
  const [showBanner, setShowBanner] = useState(false)
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    const existing = loadCookieConsent()
    if (existing) {
      setRecord(existing)
      applyConsentedScripts(existing.preferences)
    } else {
      const timer = window.setTimeout(() => setShowBanner(true), 800)
      return () => window.clearTimeout(timer)
    }
  }, [])

  const persist = useCallback((preferences: CookiePreferences) => {
    const saved = saveCookieConsent(preferences)
    setRecord(saved)
    setShowBanner(false)
    setShowModal(false)
    applyConsentedScripts(saved.preferences)
  }, [])

  const acceptAll = useCallback(() => {
    persist(ALL_ACCEPTED_PREFERENCES)
  }, [persist])

  const rejectOptional = useCallback(() => {
    persist(DEFAULT_PREFERENCES)
  }, [persist])

  const openPreferences = useCallback(() => {
    setShowModal(true)
  }, [])

  const closePreferences = useCallback(() => {
    setShowModal(false)
  }, [])

  const savePreferences = useCallback(
    (preferences: CookiePreferences) => {
      persist({ ...preferences, essential: true })
    },
    [persist]
  )

  const isCategoryEnabled = useCallback(
    (category: CookieCategory) => {
      if (category === 'essential') return true
      return record?.preferences[category] ?? false
    },
    [record]
  )

  return {
    hasConsented: record?.hasConsented ?? false,
    preferences: record?.preferences ?? DEFAULT_PREFERENCES,
    showBanner: showBanner && !record?.hasConsented,
    showModal,
    acceptAll,
    rejectOptional,
    openPreferences,
    closePreferences,
    savePreferences,
    isCategoryEnabled,
  }
}
